/***
 *
 *   INVITATIONS
 *   Invite organization members after onboarding.
 *
 **********/

import { Fragment, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { ViewContext, Card, Animate } from 'components/lib';
import { inviteOrganizationMember } from '../../lib/auth-client';
import { getApiErrorMessage } from 'api/apiClient';
import { getCurrentUsage } from '../../api/planApi';
import { getInviteEmailValidation } from '../../services/inviteEmailValidation';
import { UMButton } from '@urbanmetrix/um-kit';

type InviteUsersProps = {
  inviteEmail: string;
  invitePermission: string;
  onInviteEmailChange: (value: string) => void;
  onInvitePermissionChange: (value: string) => void;
  errorMessage: string | null;
};

export function InvitationsView() {
  const viewContext = useContext(ViewContext) as any;
  const [isStarting, setIsStarting] = useState(false);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermission, setInvitePermission] = useState('editor');
  const hasInviteInput = inviteEmail.length > 0;
  const [userUsage, setUserUsage] = useState<{ usersUsed: number | null; usersLimit: number | null } | null>(null);

  const inviteValidation = useMemo(() => getInviteEmailValidation(inviteEmail), [inviteEmail]);
  const inviteEmails = inviteValidation.emails;
  const validInviteEmails = inviteValidation.validEmails;
  const hasInvalidEmails = inviteValidation.hasInvalidEmails;

  const usersUsed = Number(userUsage?.usersUsed);
  const usersLimit = Number(userUsage?.usersLimit);
  const hasUserLimit = Number.isFinite(usersUsed) && Number.isFinite(usersLimit);
  const remainingUsers = hasUserLimit ? Math.max(usersLimit - usersUsed, 0) : null;
  const enteredInviteCount = inviteEmails.length;
  const exceedsUserLimit = remainingUsers !== null && enteredInviteCount > remainingUsers;
  const inviteErrorMessage = hasInvalidEmails
    ? 'Please enter valid email addresses.'
    : exceedsUserLimit
      ? `You can invite up to ${remainingUsers} user${remainingUsers === 1 ? '' : 's'} with your current plan. Reduce the number of email addresses before proceeding, or upgrade your plan if you need more users.`
      : null;
  const isSubmitDisabled = isStarting || isLoadingUsage || exceedsUserLimit || hasInvalidEmails;

  useEffect(() => {
    let mounted = true;

    async function loadUsage() {
      try {
        setIsLoadingUsage(true);
        const usage = await getCurrentUsage();

        if (!mounted) return;

        setUserUsage({
          usersUsed: usage?.usersUsed ?? null,
          usersLimit: usage?.usersLimit ?? null,
        });
      } catch (error) {
        if (!mounted) return;
        setUserUsage(null);
      } finally {
        if (mounted) {
          setIsLoadingUsage(false);
        }
      }
    }

    loadUsage();

    return () => {
      mounted = false;
    };
  }, []);

  async function handleStart() {
    if (hasInvalidEmails) {
      viewContext.notification.show('Please enter valid email addresses.', 'error', true, 'toast');
      return;
    }

    if (exceedsUserLimit) {
      viewContext.notification.show(
        `You can invite up to ${remainingUsers} user${remainingUsers === 1 ? '' : 's'} with your current plan.`,
        'error',
        true,
        'toast'
      );
      return;
    }

    setIsStarting(true);
    try {
      const emails = validInviteEmails;

      if (emails.length) {
        try {
          for (const email of emails) {
            await inviteOrganizationMember(email, invitePermission as 'owner' | 'admin' | 'editor' | 'viewer');
          }
          viewContext.notification.show('Invitation sent', 'success', true, 'toast');
        } catch (error) {
          console.error(error);
          viewContext.notification.show(
            getApiErrorMessage(error, 'Unable to send invitations. You can invite users later from the account page.'),
            'error',
            true,
            'toast'
          );
        }
      }

      window.location.href = '/dashboard?guidedTour=true';
    } finally {
      setIsStarting(false);
    }
  }

  return (
    <OnboardingCard>
      <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>Invite your team</h3>
      <p style={{ marginBottom: '10px' }}>You can invite users now or later from the account page.</p>
      <InviteUsers
        inviteEmail={inviteEmail}
        invitePermission={invitePermission}
        onInviteEmailChange={setInviteEmail}
        onInvitePermissionChange={setInvitePermission}
        errorMessage={inviteErrorMessage}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
        <UMButton
          text={isStarting ? 'Starting...' : hasInviteInput ? 'Start' : 'Skip'}
          onClick={handleStart}
          disabled={hasInviteInput ? isSubmitDisabled : isStarting}
          fullWidth={false}
          style={{ minWidth: '110px' }}
        />
      </div>
    </OnboardingCard>
  );
}

type OnboardingCardProps = {
  children: ReactNode;
};

function OnboardingCard({ children }: OnboardingCardProps) {
  return (
    <Animate customClass="onboarding-container--standalone">
      <Card
        title="Invitations"
        center
        restrictWidth
        extraClass="form-container onboarding-card--standalone"
        isStat={true}
        style={{ width: '100%', maxWidth: '920px', paddingBottom: '18px', position: 'relative' }}
      >
        {children}
      </Card>
    </Animate>
  );
}

function InviteUsers({
  inviteEmail,
  invitePermission,
  onInviteEmailChange,
  onInvitePermissionChange,
  errorMessage,
}: InviteUsersProps) {
  const permissionOptions = [
    { value: 'admin', label: 'admin' },
    { value: 'editor', label: 'editor' },
    { value: 'viewer', label: 'viewer' },
  ];

  return (
    <Fragment>
      <p>To invite more than one user, seperate the emails with a comma.</p>
      <div style={{ marginBottom: '12px', marginTop: '12px' }}>
        <label style={{ display: 'block', fontSize: '12px', marginBottom: '6px' }}>Email</label>
        <input
          type="text"
          value={inviteEmail}
          onChange={(event) => onInviteEmailChange(event.target.value)}
          placeholder=""
          style={{
            width: '100%',
            height: '40px',
            padding: '0 14px',
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: '6px',
            background: 'rgba(255, 255, 255, 0.35)',
          }}
        />
        {errorMessage ? (
          <p
            style={{
              marginTop: '8px',
              marginBottom: '16px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#dc2626',
              WebkitTextFillColor: '#dc2626',
            }}
          >
            {errorMessage}
          </p>
        ) : null}
      </div>

      <div style={{ marginBottom: '6px' }}>
        <label style={{ display: 'block', fontSize: '12px', marginBottom: '6px' }}>Permission</label>
        <select
          value={invitePermission}
          onChange={(event) => onInvitePermissionChange(event.target.value)}
          style={{
            width: '100%',
            height: '40px',
            padding: '0 14px',
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: '6px',
            background: 'rgba(255, 255, 255, 0.35)',
          }}
        >
          {permissionOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </Fragment>
  );
}
